// Tonk on the table in 3D: the stock and the discard pile in the middle,
// each player's spreads in front of them, face-down cards showing how many
// each player holds (face-up at the showdown), seat labels and the score.
// Table-local, floor origin (see GameTable3D).

import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Billboard, Text } from '@react-three/drei';
import { TABLE_TOP, seatXZ, seatYaw } from '../tableLayout';
import { cardTexture } from '../cardFaces';
import { SEAT_NAMES, type TonkState, type Spread } from '../../../../supabase/functions/games/tonk.ts';
import { vrColors as colors, noRaycast } from '../../VR/vrUi';
import { tonkName, isTurn, handLine } from './tonkView';
import type { Table3DProps } from '../types';

const CW = 0.08;          // card width (m)
const CH = CW * 1.4;
const Y = TABLE_TOP + 0.003;

function FlatCard({ card, x, z, y = Y }: { card: string; x: number; z: number; y?: number }) {
  const tex = useMemo(() => cardTexture(card as any), [card]);
  return (
    <mesh position={[x, y, z]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast as any}>
      <planeGeometry args={[CW, CH]} />
      <meshStandardMaterial map={tex} roughness={0.6} side={THREE.DoubleSide} />
    </mesh>
  );
}

/** A frame at seat i's edge of the table, turned so cards read the right way up for that player (local +X = their right, −Z = toward the centre). */
function AtSeat({ i, d, children }: { i: number; d: number; children: React.ReactNode }) {
  const [x, z] = seatXZ(i, d);
  return <group position={[x, 0, z]} rotation={[0, seatYaw(i) + Math.PI, 0]}>{children}</group>;
}

/** A row of cards fanned left-to-right, centred on x0. */
function Fan({ cards, x0 = 0, z = 0, step = 0.024 }: { cards: string[]; x0?: number; z?: number; step?: number }) {
  return <>{cards.map((c, k) => (
    <FlatCard key={`${c}-${k}`} card={c} x={x0 + (k - (cards.length - 1) / 2) * step} z={z} y={Y + k * 0.0006} />
  ))}</>;
}

function Spreads({ owner, spreads }: { owner: number; spreads: Spread[] }) {
  return (
    <AtSeat i={owner} d={0.3}>
      {spreads.map((sp, k) => {
        const row = Math.floor(k / 3), col = k % 3;
        const inRow = Math.min(3, spreads.length - row * 3);
        return <Fan key={sp.id} cards={sp.cards} x0={(col - (inRow - 1) / 2) * 0.17} z={-row * 0.13} step={0.022} />;
      })}
    </AtSeat>
  );
}

export function TonkTable3D({ def, table, seat }: Table3DProps) {
  const st = table?.state;
  const g = st?.g as TonkState | null | undefined;
  const playing = !!g && st?.phase !== 'lobby';
  const showdown = !!g && (g.phase === 'hand_over' || g.phase === 'game_over') && !!g.lastHand;
  const top = g?.discard[g.discard.length - 1];
  const under = g && g.discard.length > 1 ? g.discard[g.discard.length - 2] : null;
  const stockH = g ? Math.max(0.001, g.stockCount * 0.0005) : 0;
  const backTex = useMemo(() => cardTexture('back'), []);

  return (
    <>
      {playing && g && <>
        {/* Stock: a face-down stack. */}
        {g.stockCount > 0 && (
          <group position={[-0.09, TABLE_TOP + stockH / 2, 0]}>
            <mesh raycast={noRaycast as any}>
              <boxGeometry args={[CW, stockH, CH]} />
              <meshStandardMaterial color="#e9e5da" roughness={0.8} />
            </mesh>
            <mesh position={[0, stockH / 2 + 0.0006, 0]} rotation={[-Math.PI / 2, 0, 0]} raycast={noRaycast as any}>
              <planeGeometry args={[CW, CH]} />
              <meshStandardMaterial map={backTex} roughness={0.6} />
            </mesh>
          </group>
        )}
        {/* Discard pile: the top card face up (the one below peeks out). */}
        {under && <FlatCard card={under} x={0.095} z={0.012} />}
        {top && <FlatCard card={top} x={0.09} z={0} y={Y + 0.001} />}

        {g.active.map((i) => (
          <React.Fragment key={i}>
            <Spreads owner={i} spreads={g.spreads.filter((sp) => sp.owner === i)} />
            {/* Held cards: face-down count while playing (not your own — it's on your panel), face up at the showdown. */}
            {showdown && g.lastHand!.hands[i] ? (
              <AtSeat i={i} d={0.5}><Fan cards={g.lastHand!.hands[i]!} step={0.05} /></AtSeat>
            ) : !showdown && i !== seat && g.counts[i] > 0 ? (
              <AtSeat i={i} d={0.5}><Fan cards={Array(g.counts[i]).fill('back')} step={0.03} /></AtSeat>
            ) : null}
          </React.Fragment>
        ))}
      </>}

      {/* Seat labels */}
      {[0, 1, 2, 3].map((i) => {
        const s = st?.seats[i];
        if (playing && g && !g.active.includes(i)) return null;
        const [x, z] = seatXZ(i, 1.15);
        const turn = isTurn(st, g, i);
        const line2 = playing && g
          ? `${g.scores[i]} pts · ${showdown ? `total ${g.lastHand!.totals[i] ?? '–'}` : `${g.counts[i]} cards`}${turn ? (g.phase === 'draw' ? ' · drawing' : ' · playing') : ''}`
          : '';
        return (
          <Billboard key={i} position={[x, 1.45, z]}>
            <Text fontSize={0.085} color={turn ? colors.teal : '#ffffff'} outlineWidth={0.006} outlineColor="#000" raycast={noRaycast} anchorY="bottom">
              {s ? `${s.name}${s.kind === 'bot' ? ' 🤖' : ''}${i === seat ? ' (you)' : ''}` : `${SEAT_NAMES[i]} — open`}
            </Text>
            {line2 && <Text position={[0, -0.02, 0]} fontSize={0.065} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">{line2}</Text>}
          </Billboard>
        );
      })}

      {/* Scoreboard */}
      <Billboard position={[0, 1.75, 0]}>
        <Text fontSize={0.11} color="#ffffff" outlineWidth={0.007} outlineColor="#000" raycast={noRaycast} anchorY="bottom">
          {def.label}
        </Text>
        <Text position={[0, -0.02, 0]} fontSize={0.075} color="#d7e3ee" outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="top">
          {!st ? 'Loading…'
            : st.phase === 'lobby' ? `${st.seats.filter(Boolean).length}/4 seated — walk up to play`
            : g ? `Hand ${g.handNo}/${g.handsToPlay} · stock ${g.stockCount}` : ''}
        </Text>
        {g && st && showdown && (
          <Text position={[0, 0.14, 0]} fontSize={0.075} color={colors.teal} outlineWidth={0.005} outlineColor="#000" raycast={noRaycast} anchorY="bottom" maxWidth={2}>
            {st.phase === 'over'
              ? (g.winner !== null ? `${tonkName(st, g.winner)} wins the game!` : 'Drawn game')
              : handLine(st, g, seat)}
          </Text>
        )}
      </Billboard>
    </>
  );
}

export default TonkTable3D;
